import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import Button from '../components/common/Button'
import Spinner from '../components/common/Spinner'
import PostCard from '../components/social/PostCard'
import { useAutoPost } from '../hooks/useFeatures'
import { useFeedPlayer } from '../hooks/useFeedPlayer'
import { faDigits } from '../lib/numerals'
import { hasTeammatesTab } from '../lib/teams'
import { SCOPES, refusalFor, removePost, replacePost, withClap, withComment, withFollow, withRating } from '../lib/social'
import { classifyFailure } from '../services/api'
import { clapPost, commentPost, feed as fetchFeed, followUser, ratePost, unsharePost } from '../services/social'
import { myTeam } from '../services/teams'

/** Maps the feed player's own error keys to this page's translation keys. */
const PLAYER_ERROR_KEYS = {
  failed: 'playerErrors.failed',
}

/**
 * The feed (design page 10, `feed`; P-feed of the go-live plan) on B6:
 * three tabs, shared takes with play, five stars, claps and threads,
 * following. The leaderboard that used to close the page is its own tab
 * since V3-w (`/leaderboard`); a door to it stays at the foot.
 *
 * Everything is the server's. A tap is a request it does or refuses by
 * name, one at a time, and a card changes only by what the answer carries.
 * Until the owner turns the server's privacy switch on, the page is the
 * learner's own posts and the note says so; nothing here changes when the
 * switch flips.
 *
 * V5-w: a learner with a team this month gets «هم‌تیمی‌ها» (`scope=teammates`)
 * in place of «دنبال‌شده‌ها». The signal is `teams_mine`: while teams are off
 * (or without a seat) the tabs are exactly today's three.
 */
export default function Social() {
  const { t } = useTranslation('social')
  // R13: while the server posts every scored take, there is no unsharing (the feed clears daily).
  const autoPost = useAutoPost()
  const [scope, setScope] = useState('new')
  const [page, setPage] = useState(null)
  const [error, setError] = useState(null)
  const [reload, setReload] = useState(0)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)
  const [teammates, setTeammates] = useState(false)
  const player = useFeedPlayer()

  // The tab row only; a failed read keeps today's tabs.
  useEffect(() => {
    let cancelled = false
    myTeam()
      .then((data) => {
        if (cancelled || !hasTeammatesTab(data)) return
        setTeammates(true)
        // «دنبال‌شده‌ها» is not on the row any more; its place is the team's.
        setScope((current) => (current === 'following' ? 'teammates' : current))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    setError(null)
    fetchFeed({ scope })
      .then((data) => {
        if (!cancelled) setPage(data)
      })
      .catch((e) => {
        if (!cancelled) setError(e)
      })
    return () => {
      cancelled = true
    }
  }, [scope, reload])

  const { error: playerError, clearError } = player
  useEffect(() => {
    if (!playerError) return
    setNotice(t(PLAYER_ERROR_KEYS[playerError]))
    clearError()
  }, [playerError, clearError, t])

  async function request(kind, call, apply) {
    if (busy) return
    setBusy(true)
    setNotice(null)
    try {
      apply(await call())
    } catch (e) {
      setNotice(refusalFor(e, kind))
    } finally {
      setBusy(false)
    }
  }

  const update = (post, next) => setPage((current) => (current ? { ...current, items: replacePost(current.items, next(post)) } : current))

  const onClap = (post, on) => request('clap', () => clapPost(post.id, on), (result) => update(post, (p) => withClap(p, result)))
  const onRate = (post, stars) => request('rate', () => ratePost(post.id, stars), (result) => update(post, (p) => withRating(p, result)))
  const onComment = (post, text) => request('comment', () => commentPost(post.id, text), (comment) => update(post, (p) => withComment(p, comment)))
  const onFollow = (author, on) =>
    request('follow', () => followUser(author.id, on), (result) =>
      setPage((current) => (current ? { ...current, items: withFollow(current.items, author.id, result.following) } : current)),
    )
  const onUnshare = (post) =>
    request('clap', () => unsharePost(post.id), (result) => {
      if (result.removed) {
        setPage((current) => (current ? { ...current, items: removePost(current.items, post.id) } : current))
        setNotice(t('page.removedNotice'))
      }
    })

  async function loadMore() {
    if (!page?.nextCursor || busy) return
    setBusy(true)
    try {
      const next = await fetchFeed({ scope, cursor: page.nextCursor })
      setPage((current) => (current ? { ...next, items: [...current.items, ...next.items.filter((item) => !current.items.some((mine) => mine.id === item.id))] } : next))
    } catch (e) {
      setNotice(refusalFor(e, 'clap'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-md space-y-3.5">
      <header className="flex items-center gap-2">
        <div className="flex-1">
          <h1 className="text-xl font-bold text-ink">{t('page.title')}</h1>
          {page && page.items.length > 0 && (
            <p className="text-xs text-ink-2">{t('page.sharedTodaySuffix', { count: faDigits(page.sharedToday) })}</p>
          )}
        </div>
      </header>

      {page && !page.publicFeed && page.items.length > 0 && (
        <p className="rounded-xl bg-accent-tint px-3.5 py-3 text-[12.5px] leading-[1.9] text-accent-ink">
          {t('page.privateFeedNote')}
        </p>
      )}

      {/* Owner, 2026-10-14: while the server posts every scored take there is nothing to send from the archive. */}
      {!autoPost && (
      <Link
        to="/archive"
        className="flex items-center gap-3 rounded-2xl border-[1.5px] border-dashed border-line bg-surface p-3 text-[13px] font-semibold text-ink-2"
      >
        <span className="grid size-[38px] shrink-0 place-items-center rounded-[11px] bg-brand-50 text-brand-700" aria-hidden="true">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 15V3M8 7l4-4 4 4M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" />
          </svg>
        </span>
        {t('page.archiveCta')}
      </Link>
      )}

      {notice && (
        <p role="status" className="rounded-xl bg-bad-tint px-3.5 py-2.5 text-xs font-semibold text-bad-ink">
          {notice}
        </p>
      )}

      {error && !page ? (
        <div className="space-y-3">
          <div role="status" className={`rounded-xl p-3 text-sm ${classifyFailure(error) === 'offline' ? 'bg-warn-tint text-warn-ink' : 'bg-bad-tint text-bad-ink'}`}>
            {classifyFailure(error) === 'offline' ? t('page.error.offline') : t('page.error.other')}
          </div>
          <Button variant="secondary" className="w-full" onClick={() => setReload((n) => n + 1)}>
            {t('page.retry')}
          </Button>
        </div>
      ) : !page ? (
        <Spinner label={t('page.loading')} />
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-1 rounded-xl bg-surface-2 p-1">
            {SCOPES(teammates).map((option) => (
              <button
                key={option.key}
                type="button"
                aria-pressed={scope === option.key}
                onClick={() => {
                  setScope(option.key)
                  setNotice(null)
                }}
                className={`h-9 rounded-lg text-[12.5px] font-semibold transition-colors ${scope === option.key ? 'bg-surface text-brand-700 shadow-sm' : 'text-ink-2'}`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {page.items.length === 0 ? (
            <div className="flex flex-col items-center gap-4 px-2.5 py-8 text-center">
              <span className="grid size-[84px] place-items-center rounded-full bg-surface-2 text-ink-3" aria-hidden="true">
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 5h16v11H9l-4 4v-4H4z" />
                </svg>
              </span>
              <p className="text-[17px] font-bold text-ink">{t('page.empty.title')}</p>
              <p className="max-w-[280px] text-[12.5px] leading-[1.9] text-ink-2">
                {scope === 'following' ? t('page.empty.followingBody') : scope === 'teammates' ? t('page.empty.teammatesBody') : t('page.empty.otherBody')}
              </p>
            </div>
          ) : (
            <>
              {page.items.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  player={player}
                  busy={busy}
                  onClap={onClap}
                  onRate={onRate}
                  onComment={onComment}
                  onFollow={onFollow}
                  onUnshare={autoPost ? undefined : onUnshare}
                />
              ))}
              {page.nextCursor && (
                <Button variant="secondary" className="w-full" loading={busy} onClick={loadMore}>
                  {t('page.loadMore')}
                </Button>
              )}
            </>
          )}
        </div>
      )}

      <Link to="/leaderboard" className="flex h-14 items-center justify-between card-soft rounded-tile px-4 text-sm font-medium text-ink transition hover:bg-surface-2">
        <span>{t('page.leaderboardCta')}</span>
        <span className="text-xs text-ink-3">{t('page.leaderboardHint')}</span>
      </Link>
    </div>
  )
}
